import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, Phone, Mail, Clock, CreditCard, Banknote } from 'lucide-react';
import { Button } from './ui/button';

const Location = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="ubicacion"
      data-testid="location-section"
      ref={ref}
      className="py-20 md:py-32 bg-coffee relative overflow-hidden"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-terracotta rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold font-medium uppercase tracking-widest text-sm">
              Encuéntranos
            </span>
            <h2
              className="font-heading text-4xl md:text-5xl font-bold text-white mt-4 mb-8"
              data-testid="location-title"
            >
              Visítanos en Madrid
            </h2>

            {/* Info Cards */}
            <div className="space-y-6">
              {/* Address */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                data-testid="location-address"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-terracotta rounded-xl">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Dirección</h3>
                    <p className="text-white/80">
                      Calle Sierra Contraviesa 1<br />
                      Entrevías, 28053 Madrid
                    </p>
                    <a
                      href="https://maps.google.com/?q=Calle+Sierra+Contraviesa+1+Madrid+28053"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:text-gold-light text-sm mt-2 inline-block underline"
                    >
                      Ver en Google Maps
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Contact */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                data-testid="location-contact"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-terracotta rounded-xl">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Contacto</h3>
                    <a
                      href="tel:+34623513773"
                      className="text-white/80 hover:text-white block"
                    >
                      +34 623 51 37 73
                    </a>
                    <a
                      href="mailto:salkantayrestaurante@gmail.com"
                      className="text-white/80 hover:text-white text-sm flex items-center gap-2 mt-1"
                    >
                      <Mail className="w-4 h-4" />
                      salkantayrestaurante@gmail.com
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Hours */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                data-testid="location-hours"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-terracotta rounded-xl">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">Horario</h3>
                    <div className="text-white/80 text-sm space-y-1">
                      <p>Lun-Dom: 9:00 - 23:00</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Payment Methods */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex flex-wrap gap-3"
                data-testid="location-payment"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
                  <Banknote className="w-4 h-4 text-gold" />
                  <span className="text-white/80 text-sm">Efectivo</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
                  <CreditCard className="w-4 h-4 text-gold" />
                  <span className="text-white/80 text-sm">Tarjeta</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
                  <span className="text-gold font-bold text-xs">Bizum</span>
                  <span className="text-white/80 text-sm">Bizum</span>
                </div>
              </motion.div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mt-8"
            >
              <Button
                asChild
                className="bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105 shadow-xl"
                data-testid="location-whatsapp-btn"
              >
                <a
                  href="https://wa.me/34623513773?text=Hola,%20me%20gustaría%20hacer%20una%20reserva"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Reserva tu Mesa
                </a>
              </Button>
            </motion.div>
          </motion.div>

          {/* Map Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3038.8!2d-3.6493!3d40.3833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd4227e5!2sCalle%20Sierra%20Contraviesa%2C%201%2C%2028053%20Madrid!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación SALKANTAY"
                className="w-full"
                data-testid="location-map"
              />
            </div>
            
            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl hidden md:block"
            >
              <p className="font-accent text-terracotta text-xl mb-1">¡Te esperamos!</p>
              <p className="text-coffee-light text-sm">Entrevías, Madrid</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Location;
